import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { PlatformIChingConsultationDto } from '@data/_generated-api/platform-api/models/platform-i-ching-consultation-dto';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { EExportDialogComponent, EExportDialogLabels, EExportFormatOption } from '@shared/components/dialogs/e-export-dialog/e-export-dialog.component';
import { NOTIFIER } from '@shared/notifier/types';
import { SharedModule } from '@shared/shared.module';
import { injectIChingTranslate } from '../consult/iching-consult-translate';
import { hexagramBits } from '../core/iching-core';
import { HdIChingHexagramFigureComponent } from '../figure/hd-iching-hexagram-figure.component';
import { HdIChingJournalExportService, IChingExportOutcome } from './export/hd-iching-journal-export.service';
import { HdIChingImportDialogComponent } from './export/hd-iching-import-dialog.component';
import { HdIChingJournalService, ICHING_JOURNAL_PAGE_SIZE, IChingJournalQuery } from './hd-iching-journal.service';
import { parseLines } from './hd-iching-journal.util';

/** Rust na de laatste toetsaanslag in het filter (zelfde ritme als het support-overzicht). */
const FILTER_DEBOUNCE_MS = 300;

/** Wat de exportdialoog exporteert: het hele dagboek of één raadpleging. */
interface JournalExportTarget {
  readonly id?: string;
}

/** De bullets per formaat; de teksten staan onder humanDesign.iching.export.{json,md}. */
const JSON_INCLUDES = ['question', 'cast', 'reflection', 'times', 'chart'] as const;
const JSON_EXCLUDES = ['oracle', 'planets', 'chart'] as const;
const MD_INCLUDES = ['steps', 'meta', 'cast', 'oracle', 'reflection', 'planets'] as const;
/** Bij het hele dagboek staat "Meer uitleg" één keer in index.md; bij één raadpleging niet. */
const MD_INCLUDES_ALL = [...MD_INCLUDES, 'moreHelpIndex'] as const;
const MD_EXCLUDES = ['commentary', 'moreHelp', 'reimport'] as const;
const MD_EXCLUDES_ALL = ['commentary', 'reimport'] as const;

/**
 * Dagboeklijst van de Classic I Ching: eigen raadplegingen, nieuwste eerst, met paging aan
 * de serverkant ({@link ICHING_JOURNAL_PAGE_SIZE} per pagina, dezelfde `e-paginator` als
 * "Mijn meldingen") en een filter op hexagram (primair of veranderd).
 *
 * Elke wijziging van `refreshToken` herlaadt de huidige pagina (de host verhoogt hem na
 * een save). Een klik op een rij geeft de raadpleging door via `consultationSelected`; na een
 * geslaagde verwijdering meldt `consultationDeleted` het id (de host vergeet dan een open raadpleging).
 */
@Component({
  selector: 'hd-iching-journal-list',
  imports: [SharedModule, HdIChingHexagramFigureComponent, EExportDialogComponent, HdIChingImportDialogComponent],
  templateUrl: './hd-iching-journal-list.component.html',
  styleUrl: './hd-iching-journal-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdIChingJournalListComponent {
  readonly refreshToken = input(0);
  readonly consultationSelected = output<PlatformIChingConsultationDto>();
  readonly consultationDeleted = output<string>();

  private readonly journal = inject(HdIChingJournalService);
  private readonly exporter = inject(HdIChingJournalExportService);
  private readonly confirmer = inject(CONFIRMER);
  private readonly notifier = inject(NOTIFIER);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly t = injectIChingTranslate();

  protected readonly page = signal(1);
  protected readonly hexagramFilter = signal<number | undefined>(undefined);
  protected readonly rows = signal<PlatformIChingConsultationDto[]>([]);
  protected readonly total = signal(0);
  protected readonly busy = signal(false);
  protected readonly error = signal(false);
  /** Pas na de eerste respons is een lege lijst echt leeg (niet "nog aan het laden"). */
  protected readonly loaded = signal(false);

  /** Open exportdialoog: hele dagboek (geen id) of één raadpleging. */
  protected readonly exportTarget = signal<JournalExportTarget | undefined>(undefined);
  protected readonly exporting = signal(false);
  protected readonly importOpen = signal(false);

  protected readonly exportTitle = computed(() =>
    this.t(this.exportTarget()?.id ? 'humanDesign.iching.export.dialogTitleEntry' : 'humanDesign.iching.export.dialogTitle'),
  );
  protected readonly exportLabels = computed<EExportDialogLabels>(() => ({
    includes: this.t('humanDesign.iching.export.includes'),
    excludes: this.t('humanDesign.iching.export.excludes'),
    download: this.t('humanDesign.iching.export.download'),
  }));
  /** JSON (terug te importeren) en Markdown (om te lezen), met per formaat wat erin zit en wat niet. */
  protected readonly exportOptions = computed<readonly EExportFormatOption[]>(() => {
    const target = this.exportTarget();
    // Pas opbouwen wanneer de dialoog opengaat: de bronkeuze vraagt de content-service.
    if (!target) return [];
    const single = !!target.id;
    const t = this.t;
    const source = t(this.exporter.currentSource() === 'wilhelm-nl' ? 'humanDesign.iching.export.sourceWilhelmNl' : 'humanDesign.iching.export.sourceLeggeEn');
    return [
      {
        id: 'json',
        icon: 'file-code',
        title: t('humanDesign.iching.export.json.title'),
        description: t(single ? 'humanDesign.iching.export.json.descriptionEntry' : 'humanDesign.iching.export.json.descriptionAll'),
        includes: JSON_INCLUDES.map(key => t(`humanDesign.iching.export.json.includes.${key}`)),
        excludes: JSON_EXCLUDES.map(key => t(`humanDesign.iching.export.json.excludes.${key}`)),
        note: t('humanDesign.iching.export.json.note'),
      },
      {
        id: 'markdown',
        icon: 'file-lines',
        title: t('humanDesign.iching.export.md.title'),
        description: t(single ? 'humanDesign.iching.export.md.descriptionEntry' : 'humanDesign.iching.export.md.descriptionAll'),
        includes: (single ? MD_INCLUDES : MD_INCLUDES_ALL).map(key => t(`humanDesign.iching.export.md.includes.${key}`, { source })),
        excludes: (single ? MD_EXCLUDES : MD_EXCLUDES_ALL).map(key => t(`humanDesign.iching.export.md.excludes.${key}`)),
        note: t('humanDesign.iching.export.md.note'),
      },
    ];
  });

  protected readonly pageSize = ICHING_JOURNAL_PAGE_SIZE;
  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.total() / this.pageSize)));
  protected readonly parseLines = parseLines;
  protected readonly hexagramBits = hexagramBits;

  /** Alleen het laatst gestarte laden mag de lijst zetten; na destroy niemand meer. */
  private generation = 0;
  private alive = true;
  private filterDebounce: ReturnType<typeof setTimeout> | undefined;
  /** Een door de backend gecorrigeerde pagina wordt overgenomen zonder opnieuw te laden. */
  private adoptedPage: number | undefined;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.alive = false;
      clearTimeout(this.filterDebounce);
    });
    effect(() => {
      this.refreshToken();
      const page = this.page();
      this.hexagramFilter();
      untracked(() => {
        if (this.adoptedPage === page) {
          this.adoptedPage = undefined;
          return;
        }
        this.adoptedPage = undefined;
        void this.load();
      });
    });
  }

  protected setPage(page: number): void {
    if (page === this.page()) return;
    this.page.set(page);
  }

  /** Elke toetsaanslag herstart de debounce; daarna geldt het filter vanaf pagina 1. */
  protected onFilterInput(text: string): void {
    clearTimeout(this.filterDebounce);
    this.filterDebounce = setTimeout(() => this.applyFilter(toHexagram(text)), FILTER_DEBOUNCE_MS);
  }

  protected open(row: PlatformIChingConsultationDto): void {
    this.consultationSelected.emit(row);
  }

  protected openExport(row?: PlatformIChingConsultationDto): void {
    this.exportTarget.set(row?.id ? { id: row.id } : {});
  }

  protected onExportOpenChange(open: boolean): void {
    if (!open && !this.exporting()) this.exportTarget.set(undefined);
  }

  protected async runExport(format: string): Promise<void> {
    const target = this.exportTarget();
    if (!target || this.exporting()) return;
    this.exporting.set(true);
    let outcome: IChingExportOutcome;
    try {
      outcome = format === 'json' ? await this.exporter.exportJson(target.id) : await this.exporter.exportMarkdown(target.id);
    } catch {
      // De dialoog blijft open: opnieuw proberen of een ander formaat kiezen kan meteen.
      if (this.alive) this.notifier.error(this.t('humanDesign.iching.export.error'));
      return;
    } finally {
      if (this.alive) this.exporting.set(false);
    }
    if (!this.alive) return;
    this.exportTarget.set(undefined);
    if (outcome.count === 0) {
      this.notifier.info(this.t('humanDesign.iching.export.empty'));
      return;
    }
    if (outcome.planetsMissing) this.notifier.warning(this.t('humanDesign.iching.export.planetsMissing'));
  }

  protected openImport(): void {
    this.importOpen.set(true);
  }

  /** Na een import: terug naar pagina 1 (nieuwste eerst) en herladen. */
  protected onImported(): void {
    if (this.page() !== 1) {
      this.page.set(1);
      return;
    }
    void this.load();
  }

  protected async remove(row: PlatformIChingConsultationDto): Promise<void> {
    if (!row.id) return;
    const confirmed = await this.confirmer.confirm(this.t('humanDesign.iching.journal.deleteConfirm'), { color: 'danger' });
    if (!confirmed || !this.alive) return;
    try {
      await this.journal.remove(row.id);
    } catch {
      // De lijst zelf is in orde: een toast, geen laadfout.
      if (this.alive) this.notifier.error(this.t('humanDesign.iching.journal.deleteError'));
      return;
    }
    this.consultationDeleted.emit(row.id);
    await this.load();
  }

  protected async load(): Promise<void> {
    const generation = ++this.generation;
    const page = this.page();
    const hexagram = this.hexagramFilter();
    const query: IChingJournalQuery = hexagram === undefined ? { page } : { page, hexagram };
    this.busy.set(true);
    this.error.set(false);
    try {
      const result = await this.journal.queryMine(query);
      if (!this.isCurrent(generation)) return;
      this.rows.set(result.items);
      this.total.set(result.totalRecordCount);
      if (result.page >= 1 && result.page !== page) {
        this.adoptedPage = result.page;
        this.page.set(result.page);
      }
    } catch {
      if (!this.isCurrent(generation)) return;
      this.rows.set([]);
      this.error.set(true);
    } finally {
      if (this.isCurrent(generation)) {
        this.busy.set(false);
        this.loaded.set(true);
      }
    }
  }

  private applyFilter(hexagram: number | undefined): void {
    if (hexagram === this.hexagramFilter()) return;
    this.hexagramFilter.set(hexagram);
    this.page.set(1);
  }

  private isCurrent(generation: number): boolean {
    return this.alive && generation === this.generation;
  }
}

/** Geldig hexagramnummer (1..64) of geen filter. */
function toHexagram(text: string | number | null | undefined): number | undefined {
  if (text === null || text === undefined || text === '') return undefined;
  const value = Number(text);
  if (!Number.isInteger(value) || value < 1 || value > 64) return undefined;
  return value;
}
